/**
 * Keeps the header's credit number current without polling all the time:
 * - a write that reports credits spent (find email/phone anywhere) refreshes it once;
 * - background spend (agent run, bulk job) refreshes every 10s only while it runs (watchCredits);
 * - coming back to the tab refreshes it (plugin, other tabs, teammates).
 */
import axios from "axios";
import makeApi, { URLS } from "Utils/apiURL";
import { settingsStoreData } from "Stores/settingsData";

const POLL_MS = 10000;
const FOCUS_GAP_MS = 15000;
const SPENT_KEYS = ["creditsUsed", "creditsReduced", "emailCreditsReduced", "phoneCreditsReduced"];

let inflight = null;
let lastAt = 0;
let watchers = 0;
let timer = null;
let spendTimer = null;

export const refreshCredits = () => {
  if (inflight) return inflight;
  lastAt = Date.now();
  inflight = makeApi({ url: URLS.getCreditsUsage })
    .then(res => {
      const d = res?.data;
      if (d && typeof d.creditsEligible === "number") {
        settingsStoreData.setTotalRemainingCredits(Math.max(0, d.creditsEligible - (d.creditsUsed || 0)));
      }
    })
    .catch(() => {})
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

/** Background spend is running: keep the header current until the returned stop() is called. */
export const watchCredits = () => {
  watchers += 1;
  if (!timer) {
    timer = setInterval(() => {
      if (!document.hidden) refreshCredits();
    }, POLL_MS);
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    watchers -= 1;
    if (!watchers) {
      clearInterval(timer);
      timer = null;
      refreshCredits(); // the last spend lands after the final tick
    }
  };
};

const reportsSpend = d => !!d && typeof d === "object" && SPENT_KEYS.some(k => Number(d[k]) > 0);

export const spentCredits = res => {
  const method = String(res?.config?.method || "get").toLowerCase();
  const url = String(res?.config?.url || "");
  return method !== "get" && !url.endsWith("/credits") && (reportsSpend(res?.data) || reportsSpend(res?.data?.data));
};

export const installLiveCredits = () => {
  const id = axios.interceptors.response.use(res => {
    if (spentCredits(res)) {
      clearTimeout(spendTimer);
      spendTimer = setTimeout(refreshCredits, 500); // a burst of finds refreshes once
    }
    return res;
  });
  const onBack = () => {
    if (!document.hidden && Date.now() - lastAt > FOCUS_GAP_MS) refreshCredits();
  };
  window.addEventListener("focus", onBack);
  document.addEventListener("visibilitychange", onBack);
  return () => {
    axios.interceptors.response.eject(id);
    window.removeEventListener("focus", onBack);
    document.removeEventListener("visibilitychange", onBack);
  };
};
